import { useState, useEffect, useRef } from "react";
import {
  Group,
  Button,
  LoadingOverlay,
  Image,
  Stack,
  rem,
  Container,
  Grid,
  Box,
  Flex,
  Checkbox,
  Textarea,
  Text,
  Badge,
} from "@mantine/core";
import { useStore } from "../store";
import {
  IconCoins,
  IconCrop,
  IconDeviceFloppy,
  IconDownload,
  IconWand,
  IconX,
} from "@tabler/icons-react";
import ProcessingStatusBadge from "../ProcessingStatusBadge";
import mixpanel from "mixpanel-browser";
import { notifications } from "@mantine/notifications";
import {
  pollJob,
  createJob,
  applyPluginToImage,
  uploadImage,
  runOmniModificationModel,
} from "../api";
import { Redirect, useLocation } from "wouter";
import { Cropper, ImageRestriction } from "react-advanced-cropper";
import "react-advanced-cropper/dist/style.css";
import ImageInfo from "./ImageInfo";

const isDev = import.meta.env.VITE_DEV === "true";

let baseOperations = [
  { id: "removebg", label: "Remove Background", credits: 30 },
  { id: "upscale", label: "Upscale", credits: 30 },
];
if (isDev) {
  baseOperations = [
    ...baseOperations,
    { id: "test", label: "Test", credits: 1 },
  ];
  console.debug(
    "Development mode, addded test ot baseOperations",
    baseOperations
  );
}

function ImageEditor() {
  const timerIdRef = useRef(null);
  const [job, setJob] = useState({});
  const [isPolling, setIsPolling] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const profile = useStore((state) => state.profile);
  const setProfile = useStore((state) => state.setProfile);
  const imageData = useStore((state) => state.imageData);
  const setImageData = useStore((state) => state.setImageData);
  const plugins = useStore((state) => state.pluginData);
  const [editPrompt, setEditPrompt] = useState("");
  const [, setLocation] = useLocation();
  const [highQuality, setHighQuality] = useState(false);

  const cropperRef = useRef(null);

  const onCrop = () => {
    const canvas = cropperRef.current?.getCanvas();

    try {
      setIsPolling(true);
      if (canvas) {
        canvas.toBlob((blob) => {
          if (blob) {
            uploadImage(blob).then((data) => {
              console.log("Image uploaded successfully", data);
              setIsCropping(false);
              setIsPolling(false);
              setImageData(data[0]);
            });
          }
        }, "image/jpeg");
      }
    } catch (error) {
      notifications.show({
        title: "Error",
        message: "Failed to crop the image. Please try again.",
        color: "red",
      });
    } finally {
      setIsPolling(false);
    }
  };

  const applyEdit = async (prompt) => {
    if (!prompt || prompt.trim() === "") {
      notifications.show({
        title: "Error",
        message: "Please enter a valid prompt.",
        color: "red",
      });
      return;
    }

    const dto = {
      inputImageId: imageData.logicalImageId,
      prompt: prompt,
    };
    await runOmniModificationModel(dto).then((data) => {
      setJob(data);
      setIsPolling(true);
      setProfile({
        ...profile,
        credits: Math.max(0, profile.credits - 60),
      });
    });
  };

  const applyOperation = (operation) => {
    try {
      mixpanel.track("applyOperation called", {
        operationId: operation.id,
        pluginId: operation.pluginId,
      });
    } catch {}
    if (operation.pluginId) {
      applyPluginToImage(
        operation.pluginId,
        imageData.logicalImageId,
        operation.id,
        highQuality
      )
        .then((data) => {
          setJob(data);
          setIsPolling(true);
          setProfile({
            ...profile,
            credits: Math.max(0, profile.credits - operation.credits),
          });
        })
        .catch((error) => {
          notifications.show({
            title: "Error",
            message: error.message,
          });
        });
    } else {
      createJob(operation.id, imageData.logicalImageId)
        .then((data) => {
          setJob(data);
          setIsPolling(true);
          setProfile({
            ...profile,
            credits: Math.max(0, profile.credits - operation.credits),
          });
        })
        .catch((error) => {
          notifications.show({
            title: "Error",
            message: error.message,
          });
        });
    }
  };

  useEffect(() => {
    const pollingCallback = () => {
      if (!job || !job.jobId) return;

      var pending = !!(
        job &&
        job.jobId &&
        (job.status == "Processing" || job.status == "Queued")
      );

      if (job.status == "Failed") {
        setIsPolling(false);
        notifications.show({
          title: "Something went wrong!",
          message:
            "Sorry, it seems something went wrong with this job. Please try again later.",
          color: "red",
        });
        return;
      }

      if (job.status == "Success") {
        setIsPolling(false);
        if (job.results && job.results.length > 0) setImageData(job.results[0]);
        return;
      }

      if (pending == false) {
        setIsPolling(false);
        // // We're getting potentially mltiple results, TBD
        // setImageData(job.results[0]);
        // //   {
        // //   id: imageData.id,
        // //   uri: job.uri,
        // //   enhancedId: job.imageId,
        // // });
        return;
      }

      pollJob(job.jobId)
        .then((data) => {
          setJob(data);
        })
        .catch((err) => {
          console.error(err);
          setJob(null);
        });
    };

    const startPolling = () => {
      timerIdRef.current = setInterval(pollingCallback, 1500);
    };

    const stopPolling = () => {
      clearInterval(timerIdRef.current);
    };

    if (isPolling) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [isPolling, job]);

  let operations = [...baseOperations];
  if (plugins) {
    var fmap = plugins.flatMap((p) => p.actions);
    operations = operations.concat(fmap);
    // console.debug(
    //   "Operations after adding plugins",
    //   operations,
    //   fmap,
    //   plugins
    // );
  }

  const onChange = (cropper) => {
    console.log(cropper.getCoordinates(), cropper.getCanvas());
  };

  if (!imageData) return <Redirect to="/generate" />;

  const pixelImage =
    imageData.pixelImages.find((p) => p.type === "Original") ??
    imageData.pixelImages[0];

  const imageUri = pixelImage?.uri;

  return (
    <Container fluid m={0} p={0}>
      <Grid>
        <Grid.Col
          style={{ display: "flex", flexDirection: "column" }}
          span={{ base: 12, sm: 4 }}
          order={{ base: 2, sm: 1 }}
        >
          {isCropping ? (
            <Group justify="space-between" gap="xs" wrap>
              <Group gap="xs">
                <Button
                  variant="default"
                  size="xs"
                  onClick={() => setIsCropping(false)}
                  rightSection={
                    <IconX style={{ width: rem(16) }} stroke={1.5} />
                  }
                >
                  Cancel
                </Button>

                <Button
                  variant="default"
                  size="xs"
                  onClick={onCrop}
                  rightSection={
                    <IconDeviceFloppy style={{ width: rem(16) }} stroke={1.5} />
                  }
                >
                  Save Crop
                </Button>
              </Group>
            </Group>
          ) : (
            <>
              <Group gap="xs" wrap>
                <Checkbox
                  value={highQuality}
                  onChange={(event) =>
                    setHighQuality(event.currentTarget.checked)
                  }
                  label="Extra Quality"
                  size="xs"
                />

                {operations.map((operation) => (
                  <Button
                    key={operation.id}
                    variant="light"
                    size="xs"
                    onClick={() => applyOperation(operation)}
                    leftSection={
                      <Group wrap="nowrap" gap={4}>
                        <IconCoins
                          stroke={1.5}
                          style={{ width: rem(12), height: rem(12) }}
                        />
                        &nbsp;
                        {operation.credits *
                          (highQuality && operation.type === "imageFilter"
                            ? 2
                            : 1)}
                      </Group>
                    }
                    loading={isPolling}
                    loaderProps={{ type: "dots" }}
                  >
                    {operation.label}
                  </Button>
                ))}
              </Group>
              <Group mt="sm" gap="xs" wrap>
                <Button
                  leftSection={
                    <IconCrop style={{ width: rem(20) }} stroke={1.5} />
                  }
                  variant="default"
                  size="xs"
                  aria-label="Crop"
                  onClick={() => {
                    setIsCropping(true);
                  }}
                  disabled={isPolling}
                >
                  Crop for video
                </Button>

                <Button
                  component="a"
                  variant="default"
                  size="xs"
                  target="_blank"
                  href={job?.uri ?? imageData.uri}
                  aria-label="Download"
                  leftSection={
                    <IconDownload style={{ width: rem(20) }} stroke={1.5} />
                  }
                >
                  Download
                </Button>
                <Button
                  leftSection={
                    <IconX style={{ width: rem(20) }} stroke={1.5} />
                  }
                  variant="default"
                  size="xs"
                  aria-label="Close"
                  onClick={() => {
                    setImageData(null);
                    setLocation("/gallery");
                  }}
                >
                  Close
                </Button>
              </Group>
              <Stack gap="xs" mt="xl">
                <Group>
                  <Badge size="sm" color="grape">
                    New!
                  </Badge>
                  <Text fz="sm" c="dimmed" fw={600}>
                    Modify Image with AI instructions{" "}
                  </Text>
                </Group>
                <Textarea
                  placeholder="Describe what you want to change in the image, e.g. 'Make it brighter', 'Add a sunset', 'Remove the person on the left', etc."
                  autosize
                  required
                  value={editPrompt}
                  onChange={(e) => setEditPrompt(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.shiftKey === false) {
                      e.preventDefault();
                      applyEdit(editPrompt);
                    }
                  }}
                  minRows={3}
                  maxRows={6}
                />
                <Button
                  disabled={!editPrompt || isPolling}
                  onClick={() => applyEdit(editPrompt)}
                  loading={isPolling}
                  leftSection={
                    <Group wrap="nowrap" gap={4}>
                      <IconCoins
                        stroke={1.5}
                        style={{ width: rem(12), height: rem(12) }}
                      />
                      &nbsp; 60
                    </Group>
                  }
                  rightSection={
                    <IconWand
                      stroke={1.5}
                      style={{ width: rem(12), height: rem(12) }}
                    />
                  }
                  size="xs"
                >
                  Apply AI
                </Button>
              </Stack>
            </>
          )}

          <Flex flex={1} mb="md" />

          <ImageInfo imageData={imageData} pixelImage={pixelImage} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 8 }} order={{ base: 1, sm: 2 }}>
          <Stack
            style={{
              position: "relative",
              height:
                "calc(100dvh - var(--app-shell-header-height, 0px) - var(--app-shell-header-offset, 0px) - var(--app-shell-footer-height, 0px))",
            }}
            gap={0}
            p={0}
          >
            <LoadingOverlay
              visible={isPolling}
              zIndex={1000}
              overlayProps={{
                blur: 8,
                backgroundOpacity: 0.7,
                color: "#000",
                gradient:
                  "linear-gradient(145deg, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0) 100%)",
              }}
              loaderProps={{ children: <ProcessingStatusBadge job={job} /> }}
            />

            {isCropping && (
              <Cropper
                ref={cropperRef}
                aspectRatio={9 / 16}
                src={imageUri}
                imageRestriction={ImageRestriction.fitArea}
                // style={{
                //   display: isCropping ? "block" : "none",
                // }}
                onChange={onChange}
                className={"cropper"}
              />
            )}

            <Image
              radius="md"
              style={{
                display: isCropping ? "none" : "block",
                // maxHeight:
                //   "calc(100dvh - var(--app-shell-header-height, 0px) - var(--app-shell-header-offset, 0px) - var(--app-shell-footer-height, 0px) - 42px)",
                // width: "auto",
                // maxWidth: "100%",
                //bottom: 0,
                height: "100%",
              }}
              fit="contain"
              src={imageUri}
            />
            <div style={{ flex: 1 }}></div>
          </Stack>
        </Grid.Col>
      </Grid>
    </Container>
  );
}

export default ImageEditor;
